import { Device, until } from '@helpers/media';
import { isSSR } from '@helpers/ssr';
import useMedia from '@hooks/useMedia';
import IconChevron from '@img/icons/chevron.svg';
import IconFitAll from '@img/icons/icon-fit-to-screen.svg';
import IconZoomOut from '@img/icons/icon-magnifying-glass-with-minus.svg';
import IconZoomIn from '@img/icons/icon-magnifying-glass-with-plus.svg';
import IconX from '@img/icons/icon-x.svg';
import Image from '@stories/Elements/Media/Image/Image';
import { AnimatePresence } from 'framer-motion';
import * as React from 'react';
import { createPortal } from 'react-dom';
import Slider from 'react-slick';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import S from './ImageZoomModal.styles';

export interface ImageZoomModalProps {
  onClose: () => void;

  images: string[];
  initialIndex?: number;
  portalElementId?: string;
  hideBackdrop?: boolean;
}

const ImageZoomModal: React.FC<ImageZoomModalProps> = (props) => {
  const [currentIndex, setCurrentIndex] = React.useState<number>(props.initialIndex ?? 0);
  const wrapperRef = React.useRef<ReactZoomPanPinchRef | null>(null);
  const [navSlider, setNavSlider] = React.useState<Slider | null>();
  const slidesToShow = useMedia([until(Device.TabletLarge)], [5], 8);

  const handleZoomIn = (): void => {
    if (wrapperRef?.current) {
      wrapperRef.current.zoomIn();
    }
  };

  const handleZoomOut = (): void => {
    if (wrapperRef?.current) {
      wrapperRef.current.zoomOut();
    }
  };

  const handleReset = (): void => {
    if (wrapperRef?.current) {
      wrapperRef.current.resetTransform();
    }
  };

  const handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') {
      return;
    }

    props.onClose();
  };

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const renderModal = (): React.ReactNode => (
    <S.Container onClick={props.onClose} hideBackdrop={props.hideBackdrop}>
      <S.Content
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <S.CloseButton onClick={props.onClose}>
          <IconX />
        </S.CloseButton>

        <TransformWrapper minScale={0.5} maxScale={5} centerOnInit={true} ref={wrapperRef}>
          <S.MediaWrapper>
            {props.images.length > 1 ? (
              <S.CarouselControls>
                <S.CarouselControlButton direction="prev" onClick={navSlider?.slickPrev}>
                  <IconChevron />
                </S.CarouselControlButton>

                <S.CarouselControlButton direction="next" onClick={navSlider?.slickNext}>
                  <IconChevron />
                </S.CarouselControlButton>
              </S.CarouselControls>
            ) : null}

            <TransformComponent wrapperClass="wrapper" contentClass="content">
              <AnimatePresence>
                {props.images.map((image, index) => {
                  if (index !== currentIndex) {
                    return null;
                  }

                  return (
                    <S.ImageWrapper key={`image-${index}`}>
                      <S.Image src={image} />
                    </S.ImageWrapper>
                  );
                })}
              </AnimatePresence>
            </TransformComponent>
          </S.MediaWrapper>
        </TransformWrapper>

        <S.Footer>
          <S.ZoomControls>
            <S.FitToScreenControlButton onClick={handleReset}>
              <IconFitAll />
            </S.FitToScreenControlButton>
            <S.ZoomControlButton onClick={handleZoomIn}>
              <IconZoomIn />
            </S.ZoomControlButton>
            <S.ZoomControlButton onClick={handleZoomOut}>
              <IconZoomOut />
            </S.ZoomControlButton>
          </S.ZoomControls>

          {props.images.length > 1 ? (
            <S.Nav>
              <Slider
                ref={(slider) => setNavSlider(slider ?? undefined)}
                slidesToShow={props.images.length > slidesToShow ? slidesToShow : props.images.length}
                slidesToScroll={1}
                variableWidth={false}
                dots={false}
                arrows={false}
                focusOnSelect={true}
                swipeToSlide={true}
                infinite={true}
                initialSlide={props.initialIndex ?? 0}
                beforeChange={(oldIndex, newIndex) => {
                  setCurrentIndex(newIndex);
                  handleReset();
                }}
              >
                {props.images.map((img, index) => (
                  <S.NavItem key={`nav-item-${index}`}>
                    <Image src={img} sizes="100px" alt="" />
                  </S.NavItem>
                ))}
              </Slider>
            </S.Nav>
          ) : null}
        </S.Footer>
      </S.Content>
    </S.Container>
  );

  if (props.portalElementId && !isSSR) {
    const element = document.getElementById(props.portalElementId);

    if (element) {
      return createPortal(renderModal(), element);
    }
  }

  return <>{renderModal()}</>;
};

export default ImageZoomModal;
